import React, { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Collapse,
    Box,
    Typography,
    TableSortLabel,
} from "@mui/material";
import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUp from "@material-ui/icons/KeyboardArrowUp";
import ErrorIcon from "@material-ui/icons/Error";
import {
    updateDesigner,
    resetDesigner,
    SHOW_LOADING_BACKDROP,
    setDesignerLoadingBackdrop,
    changePage,
    DESIGNER_PAGE,
} from "../redux_store/appState";
import { loadJob } from "../redux_store/jobState";
import BSL_StatusChip from "../components/BSL_StatusChip";

const useStyles = makeStyles((theme) => ({
    root: {
        font: "9pt " + theme.typography.fontFamily,
    },
    noJobsFound: {
        width: "100%",
        height: "100px",
        textAlign: "center",
        paddingTop: "40px",
        fontStyle: "italic",
        color: "grey",
        fontSize: "20px",
    },
}));

function CollapsibleRow({ row, openJob, isStaff = false }) {
    const [open, setOpen] = useState(false);
    const [sortConfig, setSortConfig] = useState({
        field: "id",
        direction: "asc",
    });

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
        }).format(amount);
    };

    function formatUnixTimestamp(timestamp) {
        if (timestamp == null) return "";

        const date = new Date(timestamp);

        if (isNaN(date.getTime())) return "";

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    }

    const handleSort = (field) => {
        setSortConfig((prev) => {
            if (prev.field === field) {
                return {
                    ...prev,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                };
            }
            return { field, direction: "asc" };
        });
    };

    const sortedJobs = useMemo(() => {
        const jobsCopy = [...row.jobs];

        const compare = (a, b, field) => {
            let aVal, bVal;

            switch (field) {
                case "id":
                    aVal = a.id?.toLowerCase() || "";
                    bVal = b.id?.toLowerCase() || "";
                    break;
                case "JobName":
                    aVal = (a.JobName || "").trimStart().toLowerCase();
                    bVal = (b.JobName || "").trimStart().toLowerCase();
                    break;
                case "price":
                    aVal = a?.TotalIncGST || 0;
                    bVal = b?.TotalIncGST || 0;
                    break;
                case "cost":
                    aVal = a?.MaterialBase || 0;
                    bVal = b?.MaterialBase || 0;
                    break;
                case "lastEdited":
                    aVal = a._ts || 0;
                    bVal = b._ts || 0;
                    break;
                default:
                    aVal = "";
                    bVal = "";
            }

            if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;

            if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;

            return 0;
        };

        jobsCopy.sort((a, b) => compare(a, b, sortConfig.field));

        return jobsCopy;
    }, [row.jobs, sortConfig]);

    return (
        <>
            <TableRow>
                <TableCell align="center" style={{ borderBottomStyle: "none" }}>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpen(!open);
                        }}
                    >
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell align="center" style={{ borderBottomStyle: "none" }}>
                    <BSL_StatusChip status={row.status} disabled={true} />
                </TableCell>
                <TableCell style={{ borderBottomStyle: "none" }}>{row.numberOfJobs}</TableCell>
                <TableCell style={{ borderBottomStyle: "none" }}>{row.notPricedJobs}</TableCell>
                <TableCell style={{ borderBottomStyle: "none" }}>{formatCurrency(row.TotalIncGSTSum)}</TableCell>
                {isStaff && (
                    <TableCell style={{ borderBottomStyle: "none" }}>{formatCurrency(row.MaterialBaseSum)}</TableCell>
                )}
            </TableRow>

            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box
                            sx={{
                                margin: 1,
                                marginLeft: "50px",
                                marginRight: "50px",
                            }}
                        >
                            <Table size="small">
                                <TableHead
                                    sx={{
                                        backgroundColor: "#F4F4F4",
                                    }}
                                >
                                    <TableRow>
                                        <TableCell
                                            sortDirection={sortConfig.field === "id" ? sortConfig.direction : false}
                                        >
                                            <TableSortLabel
                                                active={sortConfig.field === "id"}
                                                direction={sortConfig.direction}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSort("id");
                                                }}
                                                style={{ fontWeight: "bold" }}
                                            >
                                                Job ID
                                            </TableSortLabel>
                                        </TableCell>

                                        <TableCell
                                            sortDirection={
                                                sortConfig.field === "JobName" ? sortConfig.direction : false
                                            }
                                        >
                                            <TableSortLabel
                                                active={sortConfig.field === "JobName"}
                                                direction={sortConfig.direction}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSort("JobName");
                                                }}
                                                style={{ fontWeight: "bold" }}
                                            >
                                                Job Name
                                            </TableSortLabel>
                                        </TableCell>

                                        <TableCell
                                            sortDirection={sortConfig.field === "price" ? sortConfig.direction : false}
                                        >
                                            <TableSortLabel
                                                active={sortConfig.field === "price"}
                                                direction={sortConfig.direction}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSort("price");
                                                }}
                                                style={{ fontWeight: "bold" }}
                                            >
                                                Quoted ($)
                                            </TableSortLabel>
                                        </TableCell>

                                        {isStaff && (
                                            <TableCell
                                                sortDirection={
                                                    sortConfig.field === "cost" ? sortConfig.direction : false
                                                }
                                            >
                                                <TableSortLabel
                                                    active={sortConfig.field === "cost"}
                                                    direction={sortConfig.direction}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSort("cost");
                                                    }}
                                                    style={{ fontWeight: "bold" }}
                                                >
                                                    Material ($)
                                                </TableSortLabel>
                                            </TableCell>
                                        )}

                                        <TableCell
                                            sortDirection={
                                                sortConfig.field === "lastEdited" ? sortConfig.direction : false
                                            }
                                        >
                                            <TableSortLabel
                                                active={sortConfig.field === "lastEdited"}
                                                direction={sortConfig.direction}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSort("lastEdited");
                                                }}
                                                style={{ fontWeight: "bold" }}
                                            >
                                                Last Edited
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {sortedJobs.map((job, index) => (
                                        <TableRow
                                            key={`${job.id}-${index}`}
                                            sx={{ cursor: "pointer" }}
                                            hover
                                            onClick={() => openJob(job)}
                                        >
                                            <TableCell component="th" scope="row">
                                                {job.id}
                                            </TableCell>
                                            <TableCell>{job.JobName}</TableCell>
                                            <TableCell>{formatCurrency(job?.TotalIncGST || 0)}</TableCell>
                                            {isStaff && <TableCell>{formatCurrency(job?.MaterialBase || 0)}</TableCell>}
                                            <TableCell>{formatUnixTimestamp(job?._ts)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

CollapsibleRow.propTypes = {
    row: PropTypes.shape({
        status: PropTypes.string.isRequired,
        numberOfJobs: PropTypes.number.isRequired,
        notPricedJobs: PropTypes.number.isRequired,
        TotalIncGSTSum: PropTypes.number.isRequired,
        jobs: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                JobName: PropTypes.string,
                TotalIncGST: PropTypes.number,
                MaterialBase: PropTypes.number,
                _ts: PropTypes.number,
            })
        ).isRequired,
    }).isRequired,
    openJob: PropTypes.func.isRequired,
};

export default function JobPricesDashboard({ tableData, loading, businessCode, containerMode = "" }) {
    const dispatch = useDispatch();
    const classes = useStyles();
    const user = useSelector((state) => state.app.user);
    const isStaff = (user?.Role || 0) >= 90;
    const [loadingJobClicked, setLoadingJobClicked] = React.useState(false);

    const openJob = (jobData) => {
        if (loadingJobClicked) return;

        setLoadingJobClicked(true);

        dispatch(changePage(DESIGNER_PAGE));
        dispatch(setDesignerLoadingBackdrop("Loading #" + jobData.id + " - " + jobData.JobName));
        dispatch(loadJob(businessCode, jobData.id, user, containerMode)).then((result) => {
            if (!result) {
                dispatch(
                    setDesignerLoadingBackdrop(
                        <div onClick={() => openJob(jobData)}>
                            <ErrorIcon style={{ fontSize: 40, color: "#ffa8a8" }} />
                            <br />
                            <Typography variant="overline" gutterBottom style={{ marginTop: "100px" }}>
                                <span>
                                    Error Loading # {jobData.id} - {jobData.JobName} ... try again?
                                </span>
                            </Typography>
                        </div>
                    )
                );
                setLoadingJobClicked(false);
            } else {
                dispatch(updateDesigner(SHOW_LOADING_BACKDROP, false));
                dispatch(resetDesigner());
                setLoadingJobClicked(false);
            }
        });
    };

    if (loading) {
        return <div>Dashboard Loading...</div>;
    }

    if (!tableData?.length) {
        return <div className={classes.noJobsFound}>- No jobs found -</div>;
    }

    return (
        <TableContainer style={{ width: "1300px", margin: "20px auto 0" }} component={Paper}>
            <Table aria-label="collapsible table">
                <TableHead
                    sx={{
                        backgroundColor: "#F4F4F4",
                    }}
                >
                    <TableRow>
                        <TableCell />
                        <TableCell style={{ fontWeight: "bold" }} align="center">
                            Status
                        </TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>Jobs Total (Count)</TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>Jobs Not Quoted (Count)</TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>Total Quoted ($)</TableCell>
                        {isStaff && <TableCell style={{ fontWeight: "bold" }}>Total Material ($)</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableData.map((row, index) => (
                        <CollapsibleRow key={`${row.status}-${index}`} row={row} openJob={openJob} isStaff={isStaff} />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

JobPricesDashboard.propTypes = {
    tableData: PropTypes.arrayOf(
        PropTypes.shape({
            status: PropTypes.string,
            numberOfJobs: PropTypes.number,
            notPricedJobs: PropTypes.number,
            TotalIncGSTSum: PropTypes.number,
            MaterialBaseSum: PropTypes.number,
            jobs: PropTypes.array,
        })
    ),
    loading: PropTypes.bool,
};
